/**
 * useHubDeepLink - Hook for Hub deep linking
 *
 * Provides URL-driven state management for Hub navigation:
 * - Reads channel, folder, entityId from URL
 * - Provides navigation functions that update URL
 * - Syncs URL state with component state
 *
 * URL Structure:
 * - /hub                                    → Hub main view (channel groups)
 * - /hub/:channel                           → Channel view (email, calls, etc.)
 * - /hub/:channel/:folder                   → Channel folder (INBOX, Sent, etc.)
 * - /hub/:channel/:folder/:subfolder        → Nested folder
 * - /hub/:channel/:folder/.../:emailId      → Email open in widget
 *
 * The last segment is detected as an entityId if it starts with a known prefix (eml-, msg_, etc.)
 *
 * Examples:
 * - /hub/email/INBOX                        → INBOX folder
 * - /hub/email/INBOX/eml-123                → Email eml-123 in INBOX
 * - /hub/email/Work/Clients/eml-456         → Email in Work/Clients folder
 *
 * Usage:
 * ```tsx
 * function HubView() {
 *   const {
 *     channel,
 *     folder,
 *     entityId,
 *     isWidgetOpen,
 *     navigateToChannel,
 *     navigateToFolder,
 *     openEmail,
 *     closeEmail,
 *     goToHub,
 *   } = useHubDeepLink();
 *
 *   return (
 *     <>
 *       <Content channel={channel} folder={folder} />
 *       {isWidgetOpen && <EmailWidget emailId={entityId} />}
 *     </>
 *   );
 * }
 * ```
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// =============================================================================
// TYPES
// =============================================================================

export type HubChannel = 'email' | 'calls' | 'messages' | 'whatsapp' | 'telegram' | 'links' | 'contacts';

/** Known entity prefixes for detecting entityId in URL
 * All IDs use hyphen format: prefix-uniqueId (e.g., eml-123, cnv-abc)
 */
const ENTITY_PREFIXES = [
  'eml-',  // emails
  'msg-',  // messages
  'cal-',  // calls
  'cnv-',  // conversations
  'cnt-',  // contacts
];

/** Email category slugs for URL routing */
const EMAIL_CATEGORIES = ['important', 'routine', 'ads', 'spam', 'classifying'] as const;
export type EmailCategorySlug = typeof EMAIL_CATEGORIES[number];

export interface UseHubDeepLinkResult {
  /** Current channel from URL (undefined = main view) */
  channel: HubChannel | undefined;

  /** Current folder path from URL (e.g., 'INBOX', 'Work/Clients') - supports nested folders */
  folder: string | undefined;

  /** Email category if in email channel (important, routine, ads, spam, classifying) */
  category: EmailCategorySlug | undefined;

  /** Entity ID if widget is open (from URL) */
  entityId: string | undefined;

  /** Whether a widget is currently open */
  isWidgetOpen: boolean;

  /** Navigate to hub main view */
  goToHub: () => void;

  /** Navigate to a channel */
  navigateToChannel: (channel: HubChannel) => void;

  /** Navigate to a folder within a channel (supports nested paths like 'Work/Clients') */
  navigateToFolder: (folder: string) => void;

  /** Navigate to an email category within the current folder */
  navigateToCategory: (category: EmailCategorySlug | null) => void;

  /** Open an entity in widget (updates URL) - works for email, contacts, calls, messages */
  openEntity: (entityId: string) => void;

  /** Close current widget (removes entityId from URL) */
  closeEntity: () => void;

  /** @deprecated Use openEntity instead */
  openEmail: (emailId: string) => void;

  /** @deprecated Use closeEntity instead */
  closeEmail: () => void;

  /** Copy current URL to clipboard */
  copyLink: () => Promise<void>;

  /** Get current full URL */
  getCurrentUrl: () => string;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Check if a segment is an entity ID (starts with known prefix)
 */
function isEntityId(segment: string): boolean {
  return ENTITY_PREFIXES.some(prefix => segment.startsWith(prefix));
}

/**
 * Check if a segment is an email category slug
 */
function isEmailCategory(segment: string): segment is EmailCategorySlug {
  return EMAIL_CATEGORIES.includes(segment as EmailCategorySlug);
}

/**
 * Encode folder path for URL (encode each segment separately)
 */
function encodeFolderPath(folderPath: string): string {
  return folderPath
    .split('/')
    .map(segment => encodeURIComponent(segment))
    .join('/');
}

/**
 * Decode folder path from URL (decode each segment)
 */
function decodeFolderPath(encodedPath: string): string {
  return encodedPath
    .split('/')
    .map(segment => decodeURIComponent(segment))
    .join('/');
}

// =============================================================================
// HOOK
// =============================================================================

export function useHubDeepLink(): UseHubDeepLinkResult {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL with support for nested folders and categories
  // URL pattern: /hub/:channel/:folder[/:subfolder...]/:category?/:entityId?
  const urlParts = useMemo(() => {
    // Remove leading /hub and split into segments
    const pathWithoutHub = location.pathname.replace(/^\/hub\/?/, '');
    if (!pathWithoutHub) {
      return { channel: undefined, folder: undefined, category: undefined, entityId: undefined };
    }

    const segments = pathWithoutHub.split('/').filter(Boolean);
    if (segments.length === 0) {
      return { channel: undefined, folder: undefined, category: undefined, entityId: undefined };
    }

    // First segment is the channel
    const rawChannel = segments[0];
    const validChannels: HubChannel[] = ['email', 'calls', 'messages', 'whatsapp', 'telegram', 'links', 'contacts'];
    const channel = validChannels.includes(rawChannel as HubChannel)
      ? (rawChannel as HubChannel)
      : undefined;

    if (!channel || segments.length === 1) {
      return { channel, folder: undefined, category: undefined, entityId: undefined };
    }

    // Remaining segments are folder path, possibly category, and possibly entityId
    const remainingSegments = segments.slice(1);

    // Check if the last segment is an entityId
    const lastSegment = remainingSegments[remainingSegments.length - 1];
    const hasEntityId = isEntityId(lastSegment);

    // For email channel, check if we have a category
    let folder: string | undefined;
    let category: EmailCategorySlug | undefined;
    let entityId: string | undefined;

    if (hasEntityId) {
      entityId = lastSegment;
      const withoutEntity = remainingSegments.slice(0, -1);

      // Check if second-to-last segment is a category (for email channel)
      if (channel === 'email' && withoutEntity.length > 0) {
        const maybeCategory = withoutEntity[withoutEntity.length - 1];
        if (isEmailCategory(maybeCategory)) {
          category = maybeCategory;
          const folderSegments = withoutEntity.slice(0, -1);
          folder = folderSegments.length > 0
            ? decodeFolderPath(folderSegments.join('/'))
            : undefined;
        } else {
          folder = withoutEntity.length > 0
            ? decodeFolderPath(withoutEntity.join('/'))
            : undefined;
        }
      } else {
        folder = withoutEntity.length > 0
          ? decodeFolderPath(withoutEntity.join('/'))
          : undefined;
      }
    } else if (channel === 'email' && isEmailCategory(lastSegment)) {
      // Last segment is a category (no entityId)
      category = lastSegment;
      const folderSegments = remainingSegments.slice(0, -1);
      folder = folderSegments.length > 0
        ? decodeFolderPath(folderSegments.join('/'))
        : undefined;
    } else {
      folder = decodeFolderPath(remainingSegments.join('/'));
      entityId = undefined;
    }

    return { channel, folder, category, entityId };
  }, [location.pathname]);

  const { channel, folder, category, entityId } = urlParts;
  const isWidgetOpen = !!entityId;

  // === NAVIGATION FUNCTIONS ===

  const goToHub = useCallback(() => {
    navigate('/hub');
  }, [navigate]);

  const navigateToChannel = useCallback(
    (newChannel: HubChannel) => {
      navigate(`/hub/${newChannel}`);
    },
    [navigate]
  );

  const navigateToFolder = useCallback(
    (newFolder: string) => {
      const currentChannel = channel || 'email';
      navigate(`/hub/${currentChannel}/${encodeFolderPath(newFolder)}`);
    },
    [channel, navigate]
  );

  /**
   * Navigate to an email category within the current folder
   * URL: /hub/email/INBOX/important
   * Pass null to clear category and show all emails
   */
  const navigateToCategory = useCallback(
    (newCategory: EmailCategorySlug | null) => {
      const currentFolder = folder || 'INBOX';
      if (newCategory) {
        navigate(`/hub/email/${encodeFolderPath(currentFolder)}/${newCategory}`);
      } else {
        navigate(`/hub/email/${encodeFolderPath(currentFolder)}`);
      }
    },
    [folder, navigate]
  );

  /**
   * Open an entity in widget - generic for all channels
   * For email: /hub/email/INBOX/eml-123 or /hub/email/INBOX/important/eml-123
   * For contacts: /hub/contacts/cnt_123
   * For calls: /hub/calls/cal_123
   */
  const openEntity = useCallback(
    (newEntityId: string) => {
      const currentChannel = channel || 'email';

      // For email channel, include folder and optionally category in path
      if (currentChannel === 'email') {
        const currentFolder = folder || 'INBOX';
        if (category) {
          navigate(`/hub/${currentChannel}/${encodeFolderPath(currentFolder)}/${category}/${newEntityId}`);
        } else {
          navigate(`/hub/${currentChannel}/${encodeFolderPath(currentFolder)}/${newEntityId}`);
        }
      } else {
        // For other channels, entity is directly under channel
        navigate(`/hub/${currentChannel}/${newEntityId}`);
      }
    },
    [channel, folder, category, navigate]
  );

  const closeEntity = useCallback(() => {
    if (!channel) {
      navigate('/hub');
    } else if (channel === 'email' && folder) {
      if (category) {
        navigate(`/hub/${channel}/${encodeFolderPath(folder)}/${category}`);
      } else {
        navigate(`/hub/${channel}/${encodeFolderPath(folder)}`);
      }
    } else {
      navigate(`/hub/${channel}`);
    }
  }, [channel, folder, category, navigate]);

  // Deprecated aliases for backward compatibility
  const openEmail = openEntity;
  const closeEmail = closeEntity;

  const copyLink = useCallback(async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }, []);

  const getCurrentUrl = useCallback(() => {
    return window.location.href;
  }, []);

  return {
    channel,
    folder,
    category,
    entityId,
    isWidgetOpen,
    goToHub,
    navigateToChannel,
    navigateToFolder,
    navigateToCategory,
    openEntity,
    closeEntity,
    openEmail, // deprecated alias
    closeEmail, // deprecated alias
    copyLink,
    getCurrentUrl,
  };
}

/**
 * Helper to build Hub URLs without navigation
 * Supports nested folder paths like 'Work/Clients'
 */
export function buildHubUrl(
  channel?: HubChannel,
  folder?: string,
  entityId?: string
): string {
  const base = '/hub';
  if (!channel) return base;
  if (!folder) return `${base}/${channel}`;
  const encodedFolder = encodeFolderPath(folder);
  if (!entityId) return `${base}/${channel}/${encodedFolder}`;
  return `${base}/${channel}/${encodedFolder}/${entityId}`;
}
